import { LineBatchMeta } from '@shared/components/ui/LineBatchMeta';
import { Product } from '@shared/components/ui/Product';
import { Spinner } from '@shared/components/ui/Spinner';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProductDetails } from '../../hooks/useProductDetail';
import type { ResolvedLocation } from '../../models';

interface ActiveLocationSectionProps {
  location: ResolvedLocation;
  onGoBack?: () => void;
}

export const ActiveLocationSection: React.FC<ActiveLocationSectionProps> = ({ location, onGoBack }) => {
  const { t } = useTranslation('relocationProcessModule');

  const productIds = useMemo(
    () => location.locationProducts?.map((p) => p.id).filter((id) => Number.isInteger(id) && id > 0) ?? [],
    [location.locationProducts],
  );
  const { productsById, isLoading } = useProductDetails(productIds);

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 min-w-0">
          <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 shrink-0">
            <span className="font-semibold">{location.code}</span>
          </div>
          <div className="text-sm text-muted-foreground">{t('active_location.hint')}</div>
        </div>

        {onGoBack && (
          <button
            type="button"
            onClick={onGoBack}
            className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-muted transition-colors shrink-0"
            title={t('active_location.change')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
          </button>
        )}
      </div>

      {location.locationProducts && location.locationProducts.length > 0 ? (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground mb-3">{t('active_location.products_in_location')}</p>
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="space-y-3">
              {location.locationProducts.map((preview) => {
                const detail = productsById.get(preview.id);
                const name =
                  (detail?.name && detail.name.trim()) ||
                  (preview.name && preview.name.trim()) ||
                  t('quantity_confirm.product_name_fallback');
                const code = detail?.code ?? preview.code;
                const image = detail?.image_hash ?? preview.image ?? '';
                const batchesForProduct =
                  location.stock_batches?.filter((s) => s.product_id === preview.id && Number.isInteger(s.batch_id)) ??
                  [];

                return (
                  <div key={preview.id} className="space-y-2">
                    <Product
                      id={preview.id}
                      src={image}
                      name={name}
                      code={code}
                      acronym={name.charAt(0).toUpperCase()}
                      showImageModal={Boolean(image)}
                      enableLineClamp={true}
                    />
                    {batchesForProduct.length > 0 ? (
                      <div className="pl-10 sm:pl-11 space-y-2">
                        {batchesForProduct.map((sb) => (
                          <LineBatchMeta
                            key={`${preview.id}-${sb.batch_id}`}
                            batch={{
                              id: sb.batch_id,
                              batch_number: sb.batch?.batch_number ?? undefined,
                              expiry_date: sb.batch?.expiry_date ?? undefined,
                            }}
                            batchId={sb.batch_id}
                            hideNumericIdFallback
                            i18nNs="relocationProcessModule"
                            lotLabelKey="panel.batch"
                            expiryLabelKey="panel.expiry"
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
